"use client"

import { useState, useEffect } from "react"
import { Card, Col, Row, Form, Button } from "react-bootstrap"
import { Formik } from "formik"
import * as Yup from "yup"
import TextInput from "../../../components/common/FormFields/TextInput.jsx"
import { addMcq, updateMcq, getMcqById } from "../../../../services/McqService.js"
import { toast } from '../../common/Toast/toast';
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom"
import { getcoursesById } from "../../../../services/CourseModule"

function AddMcq() {
  const { id } = useParams()
  const [searchParams] = useSearchParams()
  const courseIds = searchParams.get("courseIds")

  const [mcqData, setMcqData] = useState(null)
  const [courseName, setCourseName] = useState("Loading...")
  const [courseData, setCourseData] = useState(null)
  const [isEditing, setIsEditing] = useState(false)
  const [loading, setLoading] = useState(false)
  const [courseLoading, setCourseLoading] = useState(false)
  const navigate = useNavigate()

  const fetchCourseDetails = async (courseIds) => {
    try {
      setCourseLoading(true)
      const response = await getcoursesById(courseIds)
      if (response.data) {
        setCourseData(response.data)
        setCourseName(response.data.name || "Unknown Course")
      } else {
        setCourseName("Unknown Course")
        toast.error("Course not found!")
      }
    } catch (error) {
      console.error("Error fetching course details:", error)
      setCourseName("Unknown Course")
      toast.error("Failed to load course details")
    } finally {
      setCourseLoading(false)
    }
  }

  useEffect(() => {
    if (courseIds) {
      fetchCourseDetails(courseIds)
    } else {
      setCourseName("No Course Selected")
    }
  }, [courseIds])

  useEffect(() => {
    if (id) {
      fetchMCQDetails(id)
    } else {
      setMcqData(null)
      setIsEditing(false)
    }
  }, [id])

  const fetchMCQDetails = async (mcqId) => {
    try {
      setLoading(true)
      const response = await getMcqById(mcqId)
      if (response.data) {
        setMcqData(response.data)
        setIsEditing(true)
        if (response.data.courseIds && !courseIds) {
          fetchCourseDetails(response.data.courseIds)
        }
      } else {
        setMcqData(null)
        setIsEditing(false)
        toast.error("MCQ not found!")
      }
    } catch (error) {
      console.error("Error fetching MCQ details:", error)
      toast.error("Failed to load MCQ details")
      setMcqData(null)
      setIsEditing(false)
    } finally {
      setLoading(false)
    }
  }

  const validationSchema = Yup.object().shape({
    question: Yup.string()
      .required("Question is required")
      .min(10, "Question must be at least 10 characters")
      .max(500, "Question cannot exceed 500 characters"),
    option1: Yup.string().required("Option 1 is required"),
    option2: Yup.string().required("Option 2 is required"),
    option3: Yup.string().required("Option 3 is required"),
    option4: Yup.string().required("Option 4 is required"),
    correctAnswer: Yup.string()
      .required("Correct answer is required")
      .test("match-option", "Answer must match one of the provided options", function (value) {
        const { option1, option2, option3, option4 } = this.parent
        return value === option1 || value === option2 || value === option3 || value === option4
      }),
  })

  const initialValues = mcqData
    ? {
        question: mcqData.question,
        option1: mcqData.option1,
        option2: mcqData.option2,
        option3: mcqData.option3,
        option4: mcqData.option4,
        correctAnswer: mcqData.correctAnswer,
        courseIds: mcqData.courseIds || courseIds
      }
    : {
        question: "",
        option1: "",
        option2: "",
        option3: "",
        option4: "",
        correctAnswer: "",
        courseIds: courseIds
      }

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    if (!values.courseIds && !isEditing) {
      toast.error("Course ID is required!")
      setSubmitting(false)
      return
    }

    try {
      if (isEditing) {
        // Only update the current MCQ with its unique ID
        await updateMcq({
          question: values.question,
          option1: values.option1,
          option2: values.option2,
          option3: values.option3,
          option4: values.option4,
          correctAnswer: values.correctAnswer,
          courseIds:courseIds,
        }, id)
        
        toast.success("MCQ updated successfully!")
      } else {
        // For adding new MCQ
        await addMcq({
          ...values,
          courseIds: values.courseIds,
        })
        toast.success("MCQ added successfully!")
      }

      resetForm()
      setMcqData(null)
      setIsEditing(false)

      setTimeout(() => {
        const encodedCourseName = encodeURIComponent(courseName)
        navigate(`/course-wise-mcq/${courseIds}?courseName=${encodedCourseName}`)
      }, 1500)
    } catch (error) {
      console.error("Error processing MCQ:", error)
      if (error.response && error.response.status === 409) {
        toast.error("This MCQ already exists in this course!")
      } else {
        toast.error("Error processing MCQ!")
      }
    } finally {
      setSubmitting(false)
    }
  }

  const getBackLink = () => {
    const encodedCourseName = encodeURIComponent(courseName)
    if (isEditing) {
      return `/course-wise-mcq/${mcqData?.courseIds || courseIds}?courseName=${encodedCourseName}`
    }
    return courseIds ? `/course-wise-mcq/${courseIds}?courseName=${encodedCourseName}` : `/course-wise-mcq`
  }

  if (loading || courseLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "400px" }}>
        <div className="spinner-border text-warning" role="status">
          <span className="sr-only">Loading...</span>
        </div>
        <h4 className="ms-3">
          {courseLoading ? "Loading Course Details..." : "Loading MCQ..."}
        </h4>
      </div>
    )
  }

  if (!courseIds && !isEditing) {
    return (
      <div className="row">
        <div className="col-12">
          <div className="alert alert-danger text-center">
            <h4>Invalid Course</h4>
            <p>Course ID is required to add MCQs.</p>
            <Link to="/course-wise-mcq" className="btn btn-primary">
              Go to Courses
            </Link>
          </div>
        </div>
      </div>
    )
  }

  return (
    <>      <div className="row mb-3">
        <div className="col-12">
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <div>
                <h4 className="card-title mb-0">{isEditing ? `Edit MCQ ` : "Add New MCQ"}</h4>
                <small className="text-muted">
                  Course: <strong>{courseName}</strong>
                </small>
                {courseData && (
                  <div className="mt-1">
                    <small className="text-muted">
                      Duration: {courseData.duration} | 
                      Department: {courseData.departmentOfCourse} | 
                      Branch: {courseData.branchOfCourse}
                    </small>
                  </div>
                )}
              </div>
              <Link to={getBackLink()}>
                <Button variant="secondary">
                  <i className="fa fa-arrow-left me-1"></i>
                  Back to MCQ List
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>

      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ values, handleSubmit, handleChange, isSubmitting }) => (
          <Form className="p-4" onSubmit={handleSubmit}>
            <Card className="p-4">
              <div className="mb-4 border p-4 rounded">
                <Row className="mb-3">
                  <Col md={12}>
                    <h5 className="text-warning">
                      {isEditing ? "Edit MCQ" : "Add MCQ"}
                    </h5>
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col md={12}>
                    <TextInput
                      label="Enter Question *"
                      name="question"
                      as="textarea"
                      rows={3}
                      placeholder="Enter your question here..."
                    />
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col md={6}>
                    <TextInput
                      label="Enter Option 1 *"
                      name="option1"
                      placeholder="Enter option 1"
                    />
                  </Col>
                  <Col md={6}>
                    <TextInput
                      label="Enter Option 2 *"
                      name="option2"
                      placeholder="Enter option 2"
                    />
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col md={6}>
                    <TextInput
                      label="Enter Option 3 *"
                      name="option3"
                      placeholder="Enter option 3"
                    />
                  </Col>
                  <Col md={6}>
                    <TextInput
                      label="Enter Option 4 *"
                      name="option4"
                      placeholder="Enter option 4"
                    />
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col md={12}>
                    <label className="form-label fw-bold">Select Correct Answer *</label>
                    <Form.Select
                      name="correctAnswer"
                      value={values.correctAnswer}
                      onChange={handleChange}
                    >
                      <option value="">Select correct answer</option>
                      <option value={values.option1}>{values.option1 || "Option 1"}</option>
                      <option value={values.option2}>{values.option2 || "Option 2"}</option>
                      <option value={values.option3}>{values.option3 || "Option 3"}</option>
                      <option value={values.option4}>{values.option4 || "Option 4"}</option>
                    </Form.Select>
                  </Col>
                </Row>
              </div>

              <Row className="mt-4">
                <Col md={6} className="text-start">
                  <Link to={getBackLink()}>
                    <Button variant="outline-secondary" size="lg">
                      <i className="fa fa-times me-2"></i>
                      Cancel
                    </Button>
                  </Link>
                </Col>
                <Col md={6} className="text-end">
                  <Button variant="warning" type="submit" disabled={isSubmitting} size="lg" className="px-4">
                    {isSubmitting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        Processing...
                      </>
                    ) : isEditing ? (
                      <>
                        <i className="fa fa-save me-2"></i>
                        Update MCQ
                      </>
                    ) : (
                      <>
                        <i className="fa fa-plus me-2"></i>
                        Add MCQ
                      </>
                    )}
                  </Button>
                </Col>
              </Row>
            </Card>
          </Form>
        )}
      </Formik>
    </>
  )
}

export default AddMcq